/**
 * Telling a failed request apart from a refused one.
 *
 * When `fetch` never gets a response it rejects with a TypeError whose message
 * is written by the browser, not by us — Safari says "Load failed", Chrome says
 * "Failed to fetch", Firefox says "NetworkError when attempting to fetch
 * resource". Those strings are useless to a cook staring at a modal, and worse,
 * they read like the recipe was at fault when the request simply never landed.
 *
 * Anything the server actually answered with is a different case: that message
 * was written for the user and should be shown as-is.
 */

/** Browser wordings for "the request never completed". */
const NETWORK_ERROR_PATTERNS = [
  'load failed',          // Safari / WebKit
  'failed to fetch',      // Chrome / Edge
  'networkerror',         // Firefox
  'network request failed', // React Native
  'connection appears to be offline',
  'the request timed out',
];

/**
 * Did this error come from the transport rather than from our API?
 *
 * Deliberately conservative: only a TypeError (what `fetch` rejects with on a
 * transport failure) or an abort qualifies. A server message that happens to
 * contain "load failed" still reaches the user untouched.
 */
export function isNetworkError(error: unknown): boolean {
  if (!error) return false;

  const name = (error as { name?: string }).name;
  if (name === 'AbortError' || name === 'TimeoutError') return true;
  // The token refresh is itself a request. When it never landed the cook is
  // still signed in and the right word is "network", not "signed out" (#778).
  if (name === 'SessionTokenError') return (error as { reason?: string }).reason === 'transient';

  if (!(error instanceof TypeError)) return false;

  const message = (error as Error).message?.toLowerCase() ?? '';
  return NETWORK_ERROR_PATTERNS.some(pattern => message.includes(pattern));
}

/**
 * Why a request failed, from the user's point of view.
 *
 * - `timeout` — we gave up waiting (our own abort fired)
 * - `network` — the request never landed
 * - `server`  — the API answered, and the answer was no
 *
 * Only `server` carries a message worth showing verbatim. Kept separate from
 * the component so the branch that produced "Load failed" in a modal is
 * testable without a rendering harness.
 */
export type FetchFailureKind = 'timeout' | 'network' | 'server';

export function classifyFetchFailure(error: unknown, timedOut: boolean): FetchFailureKind {
  // Our own abort wins: an aborted request also looks like a network error,
  // but "taking too long" is the more accurate thing to tell the user.
  if (timedOut) return 'timeout';
  return isNetworkError(error) ? 'network' : 'server';
}

/**
 * Stable failure codes returned by /api/recipes/scrape.
 *
 * The API writes its messages in English. Matching on those strings to pick a
 * translation would break the moment someone reworded one, so the API tags each
 * failure with a code instead and the client owns the wording.
 */
export type ScrapeErrorCode =
  | 'blocked'
  | 'unscrapable'
  | 'extraction-failed'
  | 'login-wall'
  | 'invalid-url'
  | 'timeout'
  | 'auth-required'
  | 'quota-exceeded'
  | 'unknown';

const SCRAPE_ERROR_KEYS: Record<ScrapeErrorCode, string> = {
  blocked: 'addRecipe.errors.blocked',
  unscrapable: 'addRecipe.errors.unscrapable',
  'extraction-failed': 'addRecipe.errors.extractionFailed',
  'login-wall': 'addRecipe.errors.loginWall',
  'invalid-url': 'addRecipe.errors.invalidUrl',
  timeout: 'addRecipe.errors.timeout',
  // Both of these used to fall through to the server's English prose: a guest
  // who pressed Import was shown the literal string "Missing auth token".
  'auth-required': 'addRecipe.errors.authRequired',
  'quota-exceeded': 'addRecipe.errors.quotaExceeded',
  unknown: '',
};

/**
 * Turn an API failure into something worth reading in the user's language.
 *
 * The code picks the sentence; a code nobody has given a sentence to gets the
 * generic one. It used to fall back to the server's own text for an unknown
 * code, on the theory that an untranslated message beats a vague one — but
 * that text is English written for a log, and every import endpoint now
 * answers with a code (media-import included). A refusal that deserves its
 * own wording gets a code and a key, never its prose on screen.
 */
export function resolveScrapeErrorMessage(
  code: string | undefined,
  t: (key: string) => string,
): string {
  const key = code ? SCRAPE_ERROR_KEYS[code as ScrapeErrorCode] : undefined;
  if (key) {
    const translated = t(key);
    // i18next echoes the key back when it has no entry for it.
    if (translated && translated !== key) return translated;
  }
  return t('addRecipe.errors.unknown');
}

/**
 * The vocabulary `recipe_import_failed` reports to analytics.
 *
 * A superset of `ScrapeErrorCode`: the API can only describe failures it
 * answered, and a request that never landed is a category of its own.
 */
export type ImportErrorCode = ScrapeErrorCode | 'network';

/** Derived from the message table so a new code can't be added in one place only. */
const KNOWN_IMPORT_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  ...Object.keys(SCRAPE_ERROR_KEYS),
  'network',
]);

/**
 * What to report as the failure category for an import that went wrong.
 *
 * Exists because reading `err.code` alone silently loses the two failures that
 * matter most. That code is copied off the API's JSON body, so when the request
 * times out or never lands there is nothing to copy — and `JSON.stringify`
 * drops `undefined` values, so the property vanished from the payload entirely
 * rather than arriving empty. Every `recipe_import_failed` event captured
 * before this returned no category at all.
 *
 * Total by construction: always returns a code, so the property is always
 * present. Unrecognised server codes collapse to `unknown` rather than opening
 * a new dashboard bucket nobody is watching.
 */
export function resolveImportErrorCode(
  failureKind: FetchFailureKind,
  apiCode: string | undefined,
): ImportErrorCode {
  // Transport failures win: there is no server code in these cases, and how the
  // request died is the more accurate category than whatever we last heard.
  if (failureKind === 'timeout') return 'timeout';
  if (failureKind === 'network') return 'network';

  return apiCode && KNOWN_IMPORT_ERROR_CODES.has(apiCode)
    ? (apiCode as ImportErrorCode)
    : 'unknown';
}
